import {
  VisitNoteBillingAssessmentCodeDTO,
} from 'dtos/visitNoteBillingAssessmentCodeDTO';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import {
  ServiceCode,
  ServiceCodeReponse,
  ServiceType,
  VisitNoteBillingCode,
  VisitNoteServiceCode,
} from 'views/EMR/components/ServiceLookup/type';
import {
  keepPreviousData,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import {
  getApiBillingCodeSearch,
  getApiCustomServiceCodeSearch,
  getApiVisitNoteGetBillingAssessmentCodesFromVisitNotePatientIdVisitNoteId
  as getPriorICDCodes,
  getApiVisitNoteGetBillingCodesFromVisitNotePatientIdVisitNoteId,
  getApiVisitNoteGetCustomServiceCodesFromVisitNotePatientIdVisitNoteId,
  postApiCustomServiceCodeToggleFavorite,
  getApiClaimSearchNdcCodes,
  getApiSettingsGetModifierListSettings,
} from 'endpoints';
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import {
  normalizeServiceCodes,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/utils/helper';
import {
  VisitNoteBillingCodeDTO,
} from 'dtos/visitNoteBillingCodeDTO';
import {
  VisitNoteBillingCustomServiceCodeDTO,
} from 'dtos/visitNoteBillingCustomServiceCodeDTO';
import isEmpty from 'lodash/isEmpty';
import {
  CustomServiceCodeFavoriteRequestDTO,
} from 'dtos/customServiceCodeFavoriteRequestDTO';
import {
  CustomServiceCodeDTO,
} from 'dtos/customServiceCodeDTO';
import uniqueId from 'lodash/uniqueId';
import omit from 'lodash/omit';
import compact from 'lodash/compact';
import map from 'lodash/map';
import {
  NdcCodeDTOPaginatedResponseDTO,
  SettingsDTO,
} from 'dtos';
import usePrefetchedData from 'pages/Dashboard/hooks/usePrefetchedData';
import {
  ModifierListSettingDTO,
} from 'dtos/modifierListSettingDTO';

export function useUpdateBillingCode(
  type: ServiceType,
  formKeys: Record<string, string>,
) {
  const {
    watch,
    setValue,
  } = useFormContext();
  const isCPT = type === 'CPTCode';
  const [
    assessmentCodes,
    billingCodes,
  ] = isCPT ? watch([formKeys.ICDCode, formKeys.CPTCode]) : [];

  const availableCodes = useMemo(() => new Set(compact(map(assessmentCodes, 'code'))), [assessmentCodes]);

  useEffect(() => {
    if (!isCPT) {
      return;
    }

    const nextBillingCodes = (billingCodes as VisitNoteBillingCode[])?.map((billingCode) => ({
      ...billingCode,
      assessmentCodes: (assessmentCodes ?? []) as VisitNoteBillingAssessmentCodeDTO[],
      linkedICDCodes: billingCode?.linkedICDCodes?.filter(({ code }) => availableCodes.has(code ?? '')),
    }));

    if (!isEqual(nextBillingCodes, billingCodes)) {
      setValue(formKeys.CPTCode, nextBillingCodes);
    }
  }, [assessmentCodes, availableCodes, billingCodes, formKeys.CPTCode, isCPT, setValue]);
}

export function useGetPriorICDCodes(
  patientId: number | null,
  visitNoteId: number | null,
  requestSaltId: string | null = null,
): UseQueryResult<VisitNoteBillingCodeDTO[], Error> {
  return useQuery<VisitNoteBillingCodeDTO[], Error>({
    queryKey: ['prior-assesment', 'patient', patientId, visitNoteId, requestSaltId],
    queryFn: () => getPriorICDCodes(
      patientId ?? 0,
      visitNoteId ?? 0,
    ),
    enabled: ![requestSaltId, visitNoteId, patientId].some(isNil),
  });
}

export function useGetPriorCPTCodes(
  patientId: number | null,
  visitNoteId: number | null,
  requestSaltId: string | null = null,
): UseQueryResult<VisitNoteBillingCodeDTO[], Error> {
  return useQuery<VisitNoteBillingCodeDTO[], Error>({
    queryKey: ['prior-cpt', 'patient', patientId, visitNoteId, requestSaltId],
    queryFn: () => getApiVisitNoteGetBillingCodesFromVisitNotePatientIdVisitNoteId(
      patientId ?? 0,
      visitNoteId ?? 0,
    ),
    enabled: ![requestSaltId, visitNoteId, patientId].some(isNil),
  });
}

export function useGetPriorCustomSeriveCodes(
  patientId: number | null,
  visitNoteId: number | null,
  requestSaltId: string | null = null,
): UseQueryResult<VisitNoteBillingCustomServiceCodeDTO[], Error> {
  return useQuery<VisitNoteBillingCustomServiceCodeDTO[], Error>({
    queryKey: ['templates-custom-service-codes', patientId, visitNoteId, requestSaltId],
    queryFn:
    () => getApiVisitNoteGetCustomServiceCodesFromVisitNotePatientIdVisitNoteId(
      patientId ?? 0,
      visitNoteId ?? 0,
    ),
    enabled: ![requestSaltId, visitNoteId, patientId].some(isNil),
  });
}

const transportFunctionsByType = Object.freeze({
  CPTCode: getApiBillingCodeSearch,
  CustomCode: getApiCustomServiceCodeSearch,
  ICDCode: getApiBillingCodeSearch,
});

export function useGetServiceCodes(
  type: ServiceType,
  searchComponents: string,
  page: number,
  pageSize: number,
) {
  return useQuery<ServiceCodeReponse, Error>({
    queryKey: [type, 'ServiceCode', searchComponents],
    queryFn: async () => {
      const getCodes = transportFunctionsByType[type];

      const response = await getCodes({
        billingCodeType: type,
        searchComponents,
        page,
        pageSize,
      });

      const pageProperties = response?.pageProperties;
      const data = normalizeServiceCodes(response?.data as ServiceCode[], type);

      return {
        data,
        pageProperties,
      };
    },
    enabled: !isEmpty(searchComponents),
  });
}

export function useGetNdcCodes(
  search: string,
  page: number,
  pageSize: number,
) {
  return useQuery<NdcCodeDTOPaginatedResponseDTO, Error>({
    queryKey: ['ndcCode', search, page, pageSize],
    queryFn: async () => getApiClaimSearchNdcCodes({
      search,
      page,
      pageSize,
    }),
    enabled: !isEmpty(search),
    placeholderData: keepPreviousData,
  });
}

export function useToggleFavoriteCustomServices(): UseMutationResult<
CustomServiceCodeDTO | void, Error, CustomServiceCodeFavoriteRequestDTO> {
  return useMutation<CustomServiceCodeDTO | void, Error, CustomServiceCodeFavoriteRequestDTO>({
    mutationFn: (data) => postApiCustomServiceCodeToggleFavorite(data),
  });
}

const fetchPrioreCodeFunctionsByType = Object.freeze({
  CPTCode: useGetPriorCPTCodes,
  CustomCode: useGetPriorCustomSeriveCodes,
  ICDCode: useGetPriorICDCodes,
});

export function useSalt(type: ServiceType) {
  const [priorSaltId, setPriorSaltId] = useState<string | null>(null);
  const [priorData, setPriorData] = useState<VisitNoteServiceCode[] | null | undefined>(null);
  const [selectedVisitNoteId, setSelectedVisitNoteId] = useState<number | null>(null);
  const [patientId, setPatientId] = useState<number | null>(null);

  const useGetData = fetchPrioreCodeFunctionsByType[type];

  const {
    data,
    isLoading,
  } = useGetData(patientId, selectedVisitNoteId, priorSaltId);

  const fetchCodeFromVisitNote = (patientId: number | null, visitNoteId: number | null) => {
    setPriorSaltId(uniqueId(`prior_salt_cpt_codes_${visitNoteId}__`));
    setPatientId(patientId);
    setSelectedVisitNoteId(visitNoteId);
  };

  useEffect(() => {
    if (!isNil(data)) {
      const normalizedData = data?.map((code) => omit(code, [
        'keyId',
        'visitNoteBillingCodeId',
        'visitNoteBillingCustomServiceCodeId',
      ])) as VisitNoteServiceCode[];

      setPriorData(normalizedData);
      setPriorSaltId(null);
    }
  }, [data]);

  const value = useMemo(() => ({
    priorData,
    fetchCodeFromVisitNote,
    isSaltLoading: isLoading,
  }), [priorData, priorSaltId, isLoading]);

  return value;
}

type ModifierListResult = {
  modifierList: ModifierListSettingDTO[];
  isLoading: boolean;
};

export function useGetModifierList(): ModifierListResult {
  const { data, isLoading } = usePrefetchedData<SettingsDTO>({
    key: ['modifier-list'],
    fetchFn: getApiSettingsGetModifierListSettings,
  });

  return {
    modifierList: data?.modifierListSettings ?? [],
    isLoading,
  };
}
